import type { CSSObject, Theme } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'

import { brandGradientStops, brandText } from '@repo/ui'

/*
  THE SELECTED NAV ROW'S ICON CARRIES THE BRAND.

  A companion to `navIconMotion.ts`, deliberately NOT part of it. That file owns MOTION and
  says so at length; this owns one COLOUR decision. They meet only in that both are spread
  onto the same two containers, and in that they are exact complements — `navIconMotion`'s
  `NOT_INERT` excludes `.Mui-selected` from every trigger, so the row this file paints is
  precisely the row that never animates. No rule here can regress a recipe there.

  The theme's `MuiListItemButton` override already brightens the selected row's `color`, and
  lucide strokes with `currentColor`, so icon and label brighten together. This adds hue on
  top of that, for "where am I" — the one state the override's docblock says must not depend
  on where the pointer is.

  ── WHY A PAINT SERVER AND NOT A CSS GRADIENT ───────────────────────────────────────
  lucide glyphs are STROKES (`fill="none"`, `stroke="currentColor"`). A CSS
  `linear-gradient` is an <image>, and an image is not a paint for `stroke` — it can only
  ever be a background. So a gradient stroke requires an SVG `<linearGradient>` and
  `stroke: url(#id)`. Anything that sets `fill` here does nothing visible at all, since
  there is no fill to colour.

  ── ONE DEF, ONE ID, MOUNTED ONCE ───────────────────────────────────────────────────
  `<SelectedNavIconGradientDef />` is mounted a SINGLE time, at the app shell. That is what
  makes a fixed id safe. The failure mode being avoided is specific and nasty: SVG resolves
  a `url(#id)` reference to the FIRST matching id in the document, so a def duplicated per
  icon does not error — it works on whichever instance happens to be first in document
  order and silently paints every other icon from that one. "Correct on one row, wrong on
  another" is the shape of bug that survives review, so the mount is centralised rather
  than the ids being made unique per instance.

  ── userSpaceOnUse, AND WHY IT IS NOT THE SCREEN-RELATIVE TRAP ───────────────────────
  The reflex worry about `userSpaceOnUse` is that a shared def makes every icon sample a
  different slice of the ramp according to where it sits on screen. That is true when the
  icons share ONE user space — several glyphs drawn inside a single <svg> canvas.

  It does not apply here, because every lucide icon is its OWN `<svg viewBox="0 0 24 24">`,
  and a `viewBox` establishes a new user coordinate system. Per SVG's paint-server rules the
  gradient's coordinates resolve in the user space of the element REFERENCING it, not of the
  element declaring it — so `x1=0 -> x2=24` is each icon's own box, every time, regardless of
  where the icon is on screen or what pixel size it renders at (the platform's navs render
  these at 24, but the mapping would hold at 16 or 20 just the same).

  The DEFAULT, `objectBoundingBox`, is the one that misbehaves here — and not per icon, per
  PATH. A paint server in bounding-box units resolves against the bbox of each element it
  paints, and a lucide glyph is several elements. `shield-check` is the clearest case: the
  shield (16x20) and the check (6x4) would each get their own complete ramp, so the check
  would compress the full purple journey into six pixels and read as a different colour from
  the shield it sits on. `userSpaceOnUse` makes a multi-path glyph read as ONE object crossing
  ONE ramp, which is the entire point.

  Verifiable rather than asserted: the rail and the Settings nav both hold a selected row at
  the same time, at different heights on screen. If this were screen-relative they would
  sample differently. Measured, they do not — both run the identical ramp end to end.

  ── THE STOPS ARE MODE-AWARE, AND THAT IS NOT OPTIONAL ──────────────────────────────
  See `brandGradient.ts`. The shipped tab-indicator gradient ends at `#372789`, which
  measures 1.20:1 on the dark selected row — invisible. Dark takes a lifted pair off the same
  ramp. This file just asks for `brandGradientStops(theme)` and does not re-decide it.
*/

/**
 * Which treatment the selected nav icon gets.
 *
 * `'gradient'` — the brand ramp across the glyph, via the paint server below.
 * `'flat'` — a single `brand[600]`-family stroke. No def, no id, no paint server.
 *
 * Both are wired; flip this one constant to compare them. The flat option is not a
 * placeholder — at a 1px stroke weight it is a legitimate answer, and it costs nothing to
 * carry, so the choice stays visible in the source instead of being settled by whoever
 * implemented first.
 */
export const SELECTED_NAV_ICON_TREATMENT: 'gradient' | 'flat' = 'gradient'

/** The single document-wide id. Safe only because the def mounts exactly once — see above. */
export const SELECTED_NAV_ICON_GRADIENT_ID = 'vera-nav-selected-gradient'

/**
 * The shared `<linearGradient>`. Mount ONCE, at the app shell, inside the theme provider
 * (it reads the mode). Renders nothing when the treatment is `'flat'`.
 *
 * Not `display: none` — a `display:none` subtree has historically broken paint-server
 * references in some engines, and the guidance to use a zero-size absolutely-positioned
 * svg instead is why this carries `width: 0` rather than the more obvious hide. `<defs>`
 * content is never rendered anyway, so nothing is being hidden that would otherwise paint.
 */
export const SelectedNavIconGradientDef = () => {
  const theme = useTheme()
  if (SELECTED_NAV_ICON_TREATMENT !== 'gradient') return null
  const [start, end] = brandGradientStops(theme)
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        {/*
          x1/x2 span the lucide viewBox (0..24), so the ramp crosses the glyph left to
          right — the same 90deg the tab indicator runs. y1 === y2 keeps it horizontal.
        */}
        <linearGradient
          id={SELECTED_NAV_ICON_GRADIENT_ID}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="24"
          y2="0"
        >
          <stop offset="0%" stopColor={start} />
          <stop offset="100%" stopColor={end} />
        </linearGradient>
      </defs>
    </svg>
  )
}

/**
 * The selected-row rule for one surface's row class — added to the same container that
 * carries that surface's `navIconMotionSx`.
 *
 * Returns a THEME CALLBACK, not a plain object, because the `'flat'` branch needs the mode
 * to pick its token. MUI's `sx` takes an array whose entries may be functions, so a call
 * site adds this without plumbing `useTheme` of its own.
 *
 * `stroke` is set on the `.lucide` <svg>; `stroke` inherits, and lucide declares it only on
 * the root element, so the children take it without needing to be named. It beats lucide's
 * `stroke="currentColor"` for free — a presentation attribute loses to any CSS declaration.
 */
export const selectedNavIconSx =
  (rowSelector: string) =>
  (theme: Theme): CSSObject => ({
    [`& ${rowSelector}.Mui-selected .lucide`]: {
      stroke:
        SELECTED_NAV_ICON_TREATMENT === 'gradient'
          ? `url(#${SELECTED_NAV_ICON_GRADIENT_ID})`
          : // `brandText` — the measured, mode-aware brand FOREGROUND (light `brand[600]`,
            // dark `brand[400]`). Flat needs mode-awareness for exactly the reason the
            // gradient does: `brand[600]` measures 2.57:1 on the dark selected row. Reusing
            // that token rather than naming a hex keeps this file free of brand literals.
            brandText(theme),
    },
  })
